/**
 * Instanced cards — the WHOLE deck in ~1 draw call. Cards are thin boxes; the
 * top face samples a 2D atlas via per-instance UV-offset/scale instanced
 * attributes injected into MeshStandardMaterial with `onBeforeCompile`. Other
 * faces stay a neutral card-edge color.
 *
 * GUARDED: if the atlas texture/meta are absent, every card renders its back
 * color (or back texture if present) — no crash. Cards animate from the shoe to
 * their seat with a small GSAP-free lerp on mount/update (instant distribution +
 * flourish is handled by the brief deal tween).
 *
 * We never render 52 separate meshes (CLAUDE.md rule).
 */

import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import type { Card, Rank, Suit } from '@blackjack/shared';
import { CARD, layoutCards, type CardPlacement } from './cardLayout';
import { useGameStore } from '../state/store';
import { palette, assetPaths } from '../theme/tokens';
import { useSuspendingTexture, useSuspendingAtlasMeta, type AtlasMeta } from '../assets/guarded';
import { TABLE, FLAT_Z } from './seating';

/** Max simultaneously-visible cards (cap the instance buffer). */
const MAX_CARDS = 96;

/** The shoe position cards "fly" from for the deal flourish — at the dealer's
 *  right hand, on the flat edge. */
const SHOE = new THREE.Vector3(TABLE.radiusX * 0.62, TABLE.topY + 0.06, FLAT_Z + 0.12);

/** Fallback grid order (13 cols × 4 rows) used only when the atlas JSON is absent. */
const RANKS: Rank[] = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];
const SUITS: Suit[] = ['clubs', 'diamonds', 'hearts', 'spades'];

/** First-letter suit code used in the atlas frame keys (e.g. `A_C`, `10_S`). */
function suitLetter(suit: Suit): string {
  return suit.charAt(0).toUpperCase();
}

/**
 * Resolve a card → GL atlas UV offset/scale `[ox, oy, sx, sy]` such that the
 * shader's `uv * scale + offset` samples the right frame. `sx === 0` signals
 * "render the card back". The atlas JSON gives image-space `u0,v0` (top-left)..
 * `u1,v1` (bottom-right); GL v is flipped, hence `oy = 1 - v1`.
 */
/**
 * The card ART occupies an inset region of each square atlas cell (measured: the
 * art bbox is (11,2)–(53,62) within a 64px cell). Cropping the sampled UV to that
 * region makes the card fill the box face instead of stretching the square cell
 * and showing its transparent left/right margins (the "rectangle + drawing" look).
 */
const ART_INSET_X = 11 / 64;
const ART_INSET_Y = 2 / 64;

function uvForCard(card: Card | undefined, meta: AtlasMeta | null | undefined): [number, number, number, number] {
  if (!card) return [0, 0, 0, 0]; // signal: render back (sx === 0)
  if (meta) {
    const frame = meta.frames[`${card.rank}_${suitLetter(card.suit)}`];
    if (frame) {
      const fw = frame.u1 - frame.u0;
      const fh = frame.v1 - frame.v0;
      const u0 = frame.u0 + fw * ART_INSET_X;
      const u1 = frame.u1 - fw * ART_INSET_X;
      const v0 = frame.v0 + fh * ART_INSET_Y;
      const v1 = frame.v1 - fh * ART_INSET_Y;
      return [u0, 1 - v1, u1 - u0, v1 - v0];
    }
  }
  // Fallback grid (no meta): assume 13 cols × 4 rows, suit rows top→bottom.
  const col = RANKS.indexOf(card.rank);
  const row = SUITS.indexOf(card.suit);
  if (col < 0 || row < 0) return [0, 0, 0, 0];
  const sx = 1 / 13;
  const sy = 1 / 4;
  return [col * sx, 1 - (row + 1) * sy, sx, sy];
}

export function Cards() {
  const meshRef = useRef<THREE.InstancedMesh>(null);
  const matRef = useRef<THREE.MeshStandardMaterial>(null);

  // Suspending loads (see guarded.ts): the <Suspense> in Scene.tsx waits until the
  // atlas/back/meta exist, so the card material's uniforms + per-instance UVs are
  // populated with the real assets on the first render — no cold-load "back then
  // face" flip. `null` means the file is genuinely absent (graceful solid back).
  const atlas = useSuspendingTexture(assetPaths.cardAtlas, { kind: 'color' });
  const back = useSuspendingTexture(assetPaths.cardBack, { kind: 'color' });
  const atlasMeta = useSuspendingAtlasMeta(assetPaths.cardAtlasMeta);

  // Per-instance UV attributes (offset.xy, scale.xy) + a "current animated"
  // matrix buffer we lerp toward the target each frame for the deal flourish.
  const uvOffset = useMemo(() => new Float32Array(MAX_CARDS * 2), []);
  const uvScale = useMemo(() => new Float32Array(MAX_CARDS * 2), []);
  // Animation memory keyed by CARD IDENTITY (placement.key), NOT by instance slot.
  // A card keeps its animated matrix even when a hit shifts it to a different slot,
  // so existing cards (incl. the croupier's) no longer re-fly from the shoe when a
  // new card is dealt — only genuinely new cards animate in.
  const animByKey = useRef<Map<string, THREE.Matrix4>>(new Map());

  const geometry = useMemo(() => new THREE.BoxGeometry(CARD.width, CARD.thickness, CARD.height), []);

  // Subscribe to the slices that change card layout.
  const server = useGameStore((s) => s.server);
  const selfId = useGameStore((s) => s.serverAssignedId ?? s.identity?.clientId);

  const placements: CardPlacement[] = useMemo(
    () => layoutCards(server, selfId).slice(0, MAX_CARDS),
    [server, selfId],
  );

  // Inject per-instance atlas UV into the standard material once.
  useEffect(() => {
    const mat = matRef.current;
    if (!mat) return;
    mat.onBeforeCompile = (shader) => {
      shader.vertexShader =
        `attribute vec2 instUvOffset;\nattribute vec2 instUvScale;\nvarying vec2 vAtlasUv;\nvarying vec2 vRawUv;\nvarying float vHasFace;\n` +
        shader.vertexShader.replace(
          '#include <uv_vertex>',
          `#include <uv_vertex>\n vAtlasUv = uv * instUvScale + instUvOffset;\n vRawUv = uv;\n vHasFace = step(0.0001, instUvScale.x);`,
        );
      shader.fragmentShader =
        `uniform sampler2D uAtlas;\nuniform sampler2D uBack;\nuniform float uHasAtlas;\nuniform float uHasBack;\nvarying vec2 vAtlasUv;\nvarying vec2 vRawUv;\nvarying float vHasFace;\n` +
        shader.fragmentShader.replace(
          '#include <map_fragment>',
          `#include <map_fragment>\n
           // Face cards (vHasFace) sample the cropped atlas frame; face-DOWN cards
           // (scale 0) sample the back texture across the FULL face via raw uv.
           if (uHasAtlas > 0.5 && vHasFace > 0.5) {
             vec4 face = texture2D(uAtlas, vAtlasUv);
             diffuseColor.rgb = mix(diffuseColor.rgb, face.rgb, face.a);
           } else if (uHasBack > 0.5 && vHasFace < 0.5) {
             vec4 b = texture2D(uBack, vRawUv);
             diffuseColor.rgb = mix(diffuseColor.rgb, b.rgb, b.a);
           }`,
        );
      shader.uniforms.uAtlas = { value: atlas ?? null };
      shader.uniforms.uBack = { value: back ?? null };
      shader.uniforms.uHasAtlas = { value: atlas ? 1 : 0 };
      shader.uniforms.uHasBack = { value: back ? 1 : 0 };
      mat.userData.shader = shader;
    };
    mat.needsUpdate = true;
  }, [atlas, back]);

  // Keep texture uniforms in sync when guarded assets resolve later.
  useEffect(() => {
    const shader = matRef.current?.userData.shader as
      | {
          uniforms: {
            uAtlas: { value: unknown };
            uBack: { value: unknown };
            uHasAtlas: { value: number };
            uHasBack: { value: number };
          };
        }
      | undefined;
    if (!shader) return;
    shader.uniforms.uAtlas.value = atlas ?? null;
    shader.uniforms.uBack.value = back ?? null;
    shader.uniforms.uHasAtlas.value = atlas ? 1 : 0;
    shader.uniforms.uHasBack.value = back ? 1 : 0;
  }, [atlas, back]);

  // Attach instanced attributes to the geometry once.
  useLayoutEffect(() => {
    geometry.setAttribute('instUvOffset', new THREE.InstancedBufferAttribute(uvOffset, 2));
    geometry.setAttribute('instUvScale', new THREE.InstancedBufferAttribute(uvScale, 2));
  }, [geometry, uvOffset, uvScale]);

  // On layout change: set UVs per slot + seed animation memory per card identity.
  useLayoutEffect(() => {
    const mesh = meshRef.current;
    if (!mesh) return;
    mesh.count = placements.length;

    const tmp = new THREE.Vector3();
    const q = new THREE.Quaternion();
    const s = new THREE.Vector3();
    const seenKeys = new Set<string>();

    for (let i = 0; i < placements.length; i++) {
      const p = placements[i];
      if (!p) continue;
      seenKeys.add(p.key);

      const [ox, oy, sx, sy] = uvForCard(p.card, atlasMeta);
      uvOffset[i * 2] = ox;
      uvOffset[i * 2 + 1] = oy;
      uvScale[i * 2] = sx;
      uvScale[i * 2 + 1] = sy;

      // A card we've never seen flies in from the shoe; one we already track keeps
      // its current animated matrix (so a hit doesn't re-deal everyone else).
      if (!animByKey.current.has(p.key)) {
        p.matrix.decompose(tmp, q, s);
        animByKey.current.set(p.key, new THREE.Matrix4().compose(SHOE, q, s));
      }
    }
    // Forget cards that left play (round reset) so they re-animate next time.
    for (const key of [...animByKey.current.keys()]) {
      if (!seenKeys.has(key)) animByKey.current.delete(key);
    }

    (geometry.getAttribute('instUvOffset') as THREE.InstancedBufferAttribute).needsUpdate = true;
    (geometry.getAttribute('instUvScale') as THREE.InstancedBufferAttribute).needsUpdate = true;
  }, [placements, atlasMeta, uvOffset, uvScale, geometry]);

  // Animate each card's remembered matrix → its target (the deal flourish + settle).
  const dp = useMemo(() => new THREE.Vector3(), []);
  const dq = useMemo(() => new THREE.Quaternion(), []);
  const ds = useMemo(() => new THREE.Vector3(), []);
  const tp = useMemo(() => new THREE.Vector3(), []);
  const tq = useMemo(() => new THREE.Quaternion(), []);
  const ts = useMemo(() => new THREE.Vector3(), []);

  useFrame((_, delta) => {
    const mesh = meshRef.current;
    if (!mesh) return;
    const k = 1 - Math.pow(0.001, delta); // frame-rate independent lerp
    for (let i = 0; i < placements.length; i++) {
      const p = placements[i];
      if (!p) continue;
      const cur = animByKey.current.get(p.key);
      if (!cur) continue;
      cur.decompose(dp, dq, ds);
      p.matrix.decompose(tp, tq, ts);
      dp.lerp(tp, k);
      dq.slerp(tq, k);
      ds.lerp(ts, k);
      cur.compose(dp, dq, ds);
      mesh.setMatrixAt(i, cur);
    }
    mesh.instanceMatrix.needsUpdate = true;
  });

  return (
    <instancedMesh
      ref={meshRef}
      args={[geometry, undefined, MAX_CARDS]}
      castShadow
      receiveShadow
      frustumCulled={false}
    >
      <meshStandardMaterial
        ref={matRef}
        color={palette.cream}
        roughness={0.55}
        metalness={0}
      />
    </instancedMesh>
  );
}
